import { Grid, Typography, Box, CircularProgress, Paper, ClickAwayListener, Popper } from "@mui/material";
import { Stack } from "@mui/system";
import * as React from "react";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CustomChip from "../../../../shared/CustomChip";
import { AuthenticationContext } from "../../../../Context";
import { getHeaders } from "../../../../utils/HeaderHelper";
import { useFlash } from "../../../../FlashContext";
import moment from "moment";

type EligibilityStatus = "eligible" | "ineligible" | "pending" | null;
type EnrollmentStatus  = "enrolled" | "not_enrolled" | null;

interface PayerProgram {
  payer_program_id:            number;
  program_name:                string;
  eligibility_status:          EligibilityStatus;
  eligibility_determined_at:   string | null;
  qualifying_medication_names: string[];
  enrollment_status:           EnrollmentStatus;
  enrolled_at:                 string | null;
  unenrolled_at:               string | null;
}

interface Props {
  program:   PayerProgram;
  patientId: string | undefined;
}

interface EnrollmentState {
  status:       EnrollmentStatus;
  enrolledAt:   string | null;
  unenrolledAt: string | null;
}

const DATE_FORMAT = "M/D/YY";

const getEligibilityChipType = (status: EligibilityStatus) => {
  if (status === "eligible")   return "success";
  if (status === "ineligible") return "default";
  return "warning";
};

const getEligibilityLabel = (status: EligibilityStatus) => {
  if (status === "eligible")   return "Eligible";
  if (status === "ineligible") return "Ineligible";
  return "Pending";
};

const getEnrollmentChipType = (status: EnrollmentStatus) =>
  status === "enrolled" ? "success" : "default";

const getEnrollmentLabel = (status: EnrollmentStatus) =>
  status === "enrolled" ? "Enrolled" : "Not Enrolled";

const getEligibilityCaption = (
  status: EligibilityStatus,
  programName: string,
  determinedAt: string | null,
  medicationNames: string[]
): string => {
  if (!status || status === "pending") {
    return "Eligibility pending. This patient has not been evaluated for this payer program yet.";
  }
  if (status === "eligible") {
    const dateClause = determinedAt
      ? `as of ${moment(determinedAt).format(DATE_FORMAT)} `
      : "";
    const uniqueMeds = [...new Set(medicationNames)].filter(Boolean);
    if (uniqueMeds.length === 0) {
      return `Eligible ${dateClause}for ${programName}.`.trim();
    }
    const claimWord = uniqueMeds.length === 1 ? "claim" : "claims";
    return `Eligible ${dateClause}with qualifying ${programName} ${claimWord} found: ${uniqueMeds.join(", ")}.`;
  }
  if (status === "ineligible") {
    return `No qualifying ${programName} claims were found during the eligibility lookback period.`;
  }
  return "Eligibility could not be determined for this payer program.";
};

const getEnrollmentCopy = ({ status, enrolledAt, unenrolledAt }: EnrollmentState): string | null => {
  if (status === "enrolled" && enrolledAt) {
    return `Enrolled as of ${moment(enrolledAt).format(DATE_FORMAT)}`;
  }
  if (status === "not_enrolled" && unenrolledAt) {
    return `Unenrolled as of ${moment(unenrolledAt).format(DATE_FORMAT)}`;
  }
  return null;
};

const PayerProgramCard: React.FC<Props> = ({ program, patientId }) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [open, setOpen]       = React.useState(false);
  const [saving, setSaving]   = React.useState(false);
  const anchorRef             = React.useRef<HTMLDivElement>(null);

  const [enrollment, setEnrollment] = React.useState<EnrollmentState>({
    status:       program.enrollment_status,
    enrolledAt:   program.enrolled_at,
    unenrolledAt: program.unenrolled_at,
  });

  const alternateStatus: EnrollmentStatus =
    enrollment.status === "enrolled" ? "not_enrolled" : "enrolled";

  const eligibilityCaption = getEligibilityCaption(
    program.eligibility_status,
    program.program_name,
    program.eligibility_determined_at,
    program.qualifying_medication_names ?? []
  );

  const enrollmentCopy = getEnrollmentCopy(enrollment);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleEnrollmentChange = React.useCallback(async (newStatus: EnrollmentStatus) => {
    if (newStatus === enrollment.status) return;
    setSaving(true);
    try {
      const response = await fetch(
        `/patients/${patientId}/payer_programs/${program.payer_program_id}/enrollment`,
        {
          method: "PATCH",
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({ enrollment_status: newStatus }),
        }
      );
      const result = await response.json();

      if (!response.ok || result.success === false) {
        showToast({
          title: "Error",
          text: result?.error ?? "Failed to update enrollment status.",
          type: "error",
        });
        return;
      }

      const updated = result.data;
      setEnrollment({
        status:       updated.enrollment_status,
        enrolledAt:   updated.enrolled_at,
        unenrolledAt: updated.unenrolled_at,
      });
      setOpen(false);
    } catch {
      showToast({ title: "Error", text: "Failed to update enrollment status.", type: "error" });
    } finally {
      setSaving(false);
    }
  }, [patientId, program.payer_program_id, authenticationSetting.csrfToken, enrollment.status]);

  return (
    <Grid container direction="row" className="admin-header">
      <Grid item xs={12} className="box-header" borderRadius="4px" px={5} py={3} mx={2} mt={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={5}
        >
          <Stack direction="column" spacing={1}>
            <strong
              className="bolder-text"
              style={{ fontSize: "18px", textTransform: "capitalize", color: "#4A4442", fontFamily: "QuicksandBold", letterSpacing: "1px" }}
            >
              {program.program_name} &nbsp;&nbsp;
              <CustomChip
                label={getEligibilityLabel(program.eligibility_status)}
                type={getEligibilityChipType(program.eligibility_status)}
                size="small"
                sx={{ px: 2 }}
              />
            </strong>
            <div className="subdued-text">{eligibilityCaption}</div>
          </Stack>

          <Stack direction="row" spacing={3} alignItems="center">
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <Box sx={{ display: "inline-block" }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Stack direction="column" alignItems="center" spacing={0.5}>
                    <Box
                      ref={anchorRef}
                      onClick={saving ? undefined : handleToggle}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: saving ? "default" : "pointer",
                        opacity: saving ? 0.4 : 1,
                      }}
                    >
                      <CustomChip
                        label={getEnrollmentLabel(enrollment.status)}
                        type={getEnrollmentChipType(enrollment.status)}
                        size="small"
                      />
                      {open
                        ? <ArrowDropUpIcon sx={{ fontSize: "2em" }} />
                        : <ArrowDropDownIcon sx={{ fontSize: "2em" }} />}
                    </Box>
                    {enrollmentCopy && (
                      <Typography variant="body2" className="subdued-text" fontSize="0.78rem" textAlign="center">
                        {enrollmentCopy}
                      </Typography>
                    )}
                  </Stack>
                </Stack>

                <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" sx={{ zIndex: 1300 }}>
                  <Paper elevation={3} sx={{ mt: 1, p: 2, borderRadius: 2 }}>
                    {saving ? (
                      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
                        <CircularProgress size={18} />
                      </Box>
                    ) : (
                      <CustomChip
                        label={getEnrollmentLabel(alternateStatus)}
                        type={getEnrollmentChipType(alternateStatus)}
                        onClick={() => handleEnrollmentChange(alternateStatus)}
                      />
                    )}
                  </Paper>
                </Popper>
              </Box>
            </ClickAwayListener>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default PayerProgramCard;
